import { Mail, MapPin, MessageCircle, Instagram, Facebook, Clock } from "lucide-react";
import { CONTACT } from "../i18n";
import XiaohongshuIcon from "./XiaohongshuIcon";
import Reveal from "./Reveal";

export default function Contact({ t }) {
  const waLink = `https://wa.me/${CONTACT.phoneClean}?text=${encodeURIComponent(
    t.whatsappMessage,
  )}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-28 md:py-40 bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <Reveal>
            <span className="overline">{t.contact.overline}</span>
          </Reveal>
          <Reveal delay={120}>
            <h2
              className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.05] text-[#3E362E] whitespace-pre-line"
              data-testid="contact-title"
            >
              {t.contact.title}
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-8 text-[#5E554C] text-lg font-light leading-relaxed max-w-md">
              {t.contact.intro}
            </p>
          </Reveal>

          <Reveal delay={360}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="btn-accent"
                data-testid="contact-cta-whatsapp"
              >
                <MessageCircle size={16} />
                {t.contact.whatsapp}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                className="btn-primary"
                data-testid="contact-cta-email"
              >
                <Mail size={16} />
                {t.contact.email}
              </a>
            </div>
          </Reveal>

          <Reveal delay={480}>
            <form
              className="mt-14 border border-[#E8E1D9] bg-[#F5EFE6] p-6 md:p-7"
              onSubmit={(e) => {
                e.preventDefault();
                const email = e.target.elements.subscribe_email.value.trim();
                if (!email) return;
                const subject = encodeURIComponent(t.contact.newsletter.mailSubject);
                const body = encodeURIComponent(
                  `${t.contact.newsletter.mailBody}\n\n${email}`,
                );
                window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
              }}
              data-testid="newsletter-form"
            >
              <span className="overline">{t.contact.newsletter.overline}</span>
              <h3 className="font-serif text-xl md:text-2xl text-[#3E362E] mt-1.5 leading-tight">
                {t.contact.newsletter.title}
              </h3>
              <p className="text-[#5E554C] text-sm font-light mt-2 leading-relaxed">
                {t.contact.newsletter.desc}
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  name="subscribe_email"
                  placeholder={t.contact.newsletter.placeholder}
                  data-testid="newsletter-email-input"
                  className="flex-1 px-4 py-3 bg-white border border-[#E8E1D9] text-[#3E362E] placeholder:text-[#A89B8E] focus:outline-none focus:border-[#1A9BC1] transition-colors text-sm"
                />
                <button
                  type="submit"
                  className="btn-accent justify-center"
                  data-testid="newsletter-submit"
                >
                  {t.contact.newsletter.cta}
                </button>
              </div>
              <p className="text-[#8D7B68] text-xs font-light mt-3 leading-relaxed">
                {t.contact.newsletter.privacy}
              </p>
            </form>
          </Reveal>
        </div>

        <div className="lg:col-span-5 lg:col-start-8 space-y-2">
          {[
            {
              key: "address",
              icon: <MapPin size={18} />,
              label: t.contact.addressLabel,
              value: (
                <>
                  {t.contact.address}
                  <br />
                  {t.contact.city}
                </>
              ),
              href: CONTACT.mapsUrl,
            },
            {
              key: "email",
              icon: <Mail size={18} />,
              label: t.contact.emailLabel,
              value: CONTACT.email,
              href: `mailto:${CONTACT.email}`,
            },
            {
              key: "hours",
              icon: <Clock size={18} />,
              label: t.contact.hoursLabel,
              value: t.contact.hours,
            },
            {
              key: "instagram",
              icon: <Instagram size={18} />,
              label: "Instagram",
              value: "@danweihuangterapias",
              href: CONTACT.instagram,
            },
            {
              key: "facebook",
              icon: <Facebook size={18} />,
              label: "Facebook",
              value: "danweihuangenergia",
              href: CONTACT.facebook,
            },
            {
              key: "xiaohongshu",
              icon: <XiaohongshuIcon size={22} className="text-[#FF2442]" />,
              label: "小红书 · Xiaohongshu",
              value: "源 Origen",
              href: CONTACT.xiaohongshu,
            },
          ].map((item, i) => (
            <Reveal key={item.key} delay={i * 100}>
              <a
                href={item.href || "#"}
                target={item.href && item.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className="group flex items-start gap-5 py-5 border-b border-[#E8E1D9] transition-colors hover:border-[#1A9BC1]"
                data-testid={`contact-item-${item.key}`}
              >
                <div className="text-[#1A9BC1] pt-1">{item.icon}</div>
                <div className="flex-1">
                  <div className="overline !text-[#8D7B68] mb-1">{item.label}</div>
                  <div className="text-[#3E362E] text-base md:text-lg font-light leading-snug group-hover:text-[#1A9BC1] transition-colors">
                    {item.value}
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
